import {
  BillingConfigEntry,
  useSiteBillingConfig,
} from "@inrange/building-manager-api-client";
import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

const GAP_FILL_METHODS: Record<string, string> = {
  full_forecast: "Full forecast (generation + demand)",
  forecast_demand_only: "Forecast demand only (real generation)",
  no_override: "No override (use real data)",
  real_gen_from_inverters: "Generation from inverters (real demand)",
  // Reduxi only: spread a cumulative-meter gap spike across the bins the gap
  // spanned, conserving the total (monthly billed volume unchanged).
  redistribute_gap_spike: "Redistribute gap spike (spread meter spike, keep total)",
};

// Which gap-handling methods each measurement type supports. The forecast-based
// methods depend on typical-year curves that BMA only supplies for SolarEdge. Both
// SolarEdge and Reduxi sites can swap the (sometimes faulty) PV generation meter for
// SolarEdge inverter generation over a timespan - e.g. Parsonage, where the
// /energyDetails feed was inflated ~30% from 8 May 2026 (INRT-2489).
// redistribute_gap_spike is Reduxi-only (the redistribution lives in the Reduxi
// billing path in energy-data-processing).
const METHODS_BY_MEASUREMENT_TYPE: Record<string, string[]> = {
  solaredge: [
    "full_forecast",
    "forecast_demand_only",
    "no_override",
    "real_gen_from_inverters",
  ],
  reduxi: ["real_gen_from_inverters", "no_override", "redistribute_gap_spike"],
};

interface EntryForm {
  start: string;
  end: string;
  method: string;
  demandScalingFactor: number;
}

const emptyForm = (defaultMethod: string): EntryForm => ({
  start: "",
  end: "",
  method: defaultMethod,
  demandScalingFactor: 1.0,
});

const EntryFormRow = ({
  form,
  onChange,
  onSave,
  onCancel,
  isSaving,
  saveLabel,
  methodOptions,
}: {
  form: EntryForm;
  onChange: (form: EntryForm) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  saveLabel: string;
  methodOptions: string[];
}) => (
  <tr>
    <td>
      <Form.Control
        type="text"
        size="sm"
        placeholder="YYYY-MM-DD HH:MM"
        value={form.start}
        onChange={(e) => onChange({ ...form, start: e.target.value })}
      />
    </td>
    <td>
      <Form.Control
        type="text"
        size="sm"
        placeholder="YYYY-MM-DD HH:MM"
        value={form.end}
        onChange={(e) => onChange({ ...form, end: e.target.value })}
      />
    </td>
    <td>
      <Form.Select
        size="sm"
        value={form.method}
        onChange={(e) => onChange({ ...form, method: e.target.value })}
      >
        {/* Always include the current value so editing an entry whose method
            isn't in the site-type list never silently re-selects the first option. */}
        {(methodOptions.includes(form.method)
          ? methodOptions
          : [form.method, ...methodOptions]
        ).map((value) => (
          <option key={value} value={value}>
            {GAP_FILL_METHODS[value] ?? value}
          </option>
        ))}
      </Form.Select>
    </td>
    <td>
      <Form.Control
        type="number"
        size="sm"
        step="0.01"
        min="0"
        max="10"
        value={form.demandScalingFactor}
        onChange={(e) =>
          onChange({
            ...form,
            demandScalingFactor: parseFloat(e.target.value) || 1.0,
          })
        }
        style={{ width: "80px" }}
      />
    </td>
    <td></td>
    <td>
      <div className="d-flex gap-1">
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={isSaving || !form.start || !form.end}
        >
          {isSaving ? (
            <Spinner as="span" animation="border" size="sm" />
          ) : (
            saveLabel
          )}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </div>
    </td>
  </tr>
);

const DataGapHandling = ({
  siteId,
  selectedEsysUuid,
  startDate,
  endDate,
  measurementType,
}: {
  siteId: string;
  selectedEsysUuid: string | undefined;
  startDate: string;
  endDate: string;
  measurementType: string;
}) => {
  const availableMethods = useMemo(
    () => METHODS_BY_MEASUREMENT_TYPE[measurementType] ?? [],
    [measurementType]
  );
  const isSupported = availableMethods.length > 0;
  const defaultMethod = availableMethods[0] ?? "no_override";

  const { billingConfig, createEntry, updateEntry, deleteEntry } =
    useSiteBillingConfig(isSupported ? siteId : undefined);

  const [addingForm, setAddingForm] = useState<EntryForm | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingForm, setEditingForm] = useState<EntryForm>(
    emptyForm(defaultMethod)
  );
  const [error, setError] = useState<string | null>(null);

  const allEntries = useMemo(
    () => billingConfig.data?.entries ?? [],
    [billingConfig.data?.entries]
  );

  const visibleEntries = useMemo(() => {
    return allEntries.filter((e) => {
      if (selectedEsysUuid && e.esysUuid !== selectedEsysUuid) return false;
      if (startDate && endDate) {
        // Entry start/end are "YYYY-MM-DD HH:MM" datetimes; startDate/endDate are
        // date-only. Compare the date portions, else an entry that starts on the
        // end-date day is wrongly excluded ("2026-06-30 14:00" < "2026-06-30"
        // lexically, because the date-only string is a prefix).
        const startDay = e.start.slice(0, 10);
        const endDay = e.end.slice(0, 10);
        return endDay >= startDate && startDay <= endDate;
      }
      return true;
    });
  }, [allEntries, selectedEsysUuid, startDate, endDate]);

  const deletableBoundary = useMemo(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return prevMonth.toISOString().substring(0, 10);
  }, []);

  const isEntryDeletable = useCallback(
    (entry: BillingConfigEntry) => entry.start >= deletableBoundary,
    [deletableBoundary]
  );

  const handleCreate = async () => {
    if (!selectedEsysUuid || !addingForm) return;
    setError(null);
    try {
      await createEntry.mutateAsync({
        esysUuid: selectedEsysUuid,
        ...addingForm,
      });
      setAddingForm(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create entry");
    }
  };

  const startEditing = (entry: BillingConfigEntry) => {
    setEditingId(entry.id);
    setEditingForm({
      start: entry.start,
      end: entry.end,
      method: entry.method,
      demandScalingFactor: entry.demandScalingFactor,
    });
    setAddingForm(null);
    setError(null);
  };

  const handleUpdate = async () => {
    if (editingId === null || !selectedEsysUuid) return;
    setError(null);
    try {
      await updateEntry.mutateAsync({
        entryId: editingId,
        request: { esysUuid: selectedEsysUuid, ...editingForm },
      });
      setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update entry");
    }
  };

  const handleDelete = async (entryId: number) => {
    setError(null);
    try {
      await deleteEntry.mutateAsync(entryId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete entry");
    }
  };

  const isSaving =
    createEntry.isLoading || updateEntry.isLoading || deleteEntry.isLoading;
  const isFormOpen = addingForm !== null || editingId !== null;

  return (
    <Row className="mt-4">
      <Col>
        <div className="d-flex align-items-center gap-2 mb-2">
          <h5 className="mb-0">Data gap handling</h5>
          {isSupported && selectedEsysUuid && !isFormOpen && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setAddingForm(emptyForm(defaultMethod));
                setEditingId(null);
              }}
            >
              + Add gap
            </Button>
          )}
        </div>

        {!isSupported && (
          <p className="text-muted">
            Not supported for this site type (SolarEdge and Reduxi only).
          </p>
        )}

        {isSupported && !selectedEsysUuid && (
          <p className="text-muted">
            Select a specific electrical system to manage data gaps.
          </p>
        )}

        {isSupported &&
          selectedEsysUuid &&
          visibleEntries.length === 0 &&
          addingForm === null && (
            <p className="text-muted">
              No data gaps configured for this system in the selected period.
            </p>
          )}

        {(visibleEntries.length > 0 || addingForm !== null) && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Start (local)</th>
                <th>End (local)</th>
                <th>Method</th>
                <th>Demand scale</th>
                <th>Created by</th>
                <th style={{ width: "150px" }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((entry) =>
                editingId === entry.id ? (
                  <EntryFormRow
                    key={entry.id}
                    form={editingForm}
                    onChange={setEditingForm}
                    onSave={handleUpdate}
                    onCancel={() => setEditingId(null)}
                    isSaving={updateEntry.isLoading}
                    saveLabel="Save"
                    methodOptions={availableMethods}
                  />
                ) : (
                  <tr key={entry.id}>
                    <td>{entry.start}</td>
                    <td>{entry.end}</td>
                    <td>
                      <code>
                        {GAP_FILL_METHODS[entry.method] ?? entry.method}
                      </code>
                    </td>
                    <td>{entry.demandScalingFactor}</td>
                    <td>{entry.createdBy}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => startEditing(entry)}
                          disabled={isSaving || isFormOpen}
                        >
                          Edit
                        </Button>
                        {isEntryDeletable(entry) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            disabled={isSaving || isFormOpen}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
              {addingForm !== null && (
                <EntryFormRow
                  form={addingForm}
                  onChange={setAddingForm}
                  onSave={handleCreate}
                  onCancel={() => setAddingForm(null)}
                  isSaving={createEntry.isLoading}
                  saveLabel="Add"
                  methodOptions={availableMethods}
                />
              )}
            </tbody>
          </Table>
        )}

        {allEntries.length > visibleEntries.length && (
          <p className="text-muted" style={{ fontSize: "12px" }}>
            Showing {visibleEntries.length} of {allEntries.length} entries
            (filtered to selected system and date range)
          </p>
        )}

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            className="mt-2"
          >
            {error}
          </Alert>
        )}
      </Col>
    </Row>
  );
};

export default DataGapHandling;
